import { zodResolver } from '@hookform/resolvers/zod';
import { ContactRequestSchema, type ContactRequest } from '@platform/types';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';

import { useBrand } from '@/brand/brand-provider';
import { FormField } from '@/components/form-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { apiFetch } from '@/lib/api-client';
import { getAttribution } from '@/lib/attribution';
import { Seo } from '@/lib/seo';

/**
 * Public contact page behind the marketing nav's "Contact" link. Submits to
 * the unauthenticated `POST /v1/contact` endpoint; also the destination for
 * the pricing page's Custom plan card.
 */
// Prefill the intake by intent so DFY/Managed CTAs land pre-labelled. Anything
// unrecognized falls through to a blank message.
const TOPIC_PREFILL: Record<string, string> = {
  dfy: "I'd like the done-for-you setup ($99). Please set up my AI receptionist for me. My business: ",
  managed:
    "I'm interested in the Managed service (you run and tune it monthly). My business: ",
};

export function ContactPage(): JSX.Element {
  const brand = useBrand();
  const [params] = useSearchParams();
  const topic = params.get('topic') ?? '';
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ContactRequest>({
    resolver: zodResolver(ContactRequestSchema),
    defaultValues: {
      name: '',
      email: '',
      company: '',
      phone: '',
      message: TOPIC_PREFILL[topic] ?? '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await apiFetch('/contact', {
        method: 'POST',
        body: {
          ...values,
          company: values.company?.trim() ? values.company.trim() : undefined,
          phone: values.phone?.trim() ? values.phone.trim() : undefined,
          attribution: getAttribution(),
        },
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      );
    }
  });

  return (
    <section className="container max-w-2xl py-10 sm:py-14">
      <Seo
        title={`Contact us — ${brand.displayName}`}
        description="Questions about plans, higher call volumes, or a custom setup? Tell us what you need and we'll get back to you."
        path="/contact"
      />

      <div className="space-y-2 text-center">
        <h1 className="text-4xl font-bold tracking-tight">
          {topic === 'dfy'
            ? 'Get your AI receptionist set up for you'
            : topic === 'managed'
              ? 'Let us run it for you'
              : 'Talk to us'}
        </h1>
        <p className="text-muted-foreground">
          {topic === 'dfy'
            ? 'Tell us about your business and we’ll build it, test it, and take it live in 72 hours. We reply within one business day.'
            : topic === 'managed'
              ? 'We set it up and keep it sharp every month — reviewing calls and tuning answers. Tell us about your business below.'
              : 'Need a custom plan, higher call volumes, or have a question before signing up? Send us a note — we reply within one business day.'}
        </p>
      </div>

      {submitted ? (
        <div className="mt-12 rounded-2xl border border-border bg-muted/20 p-8 text-center">
          <h2 className="text-lg font-semibold">Message received</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Thanks for reaching out — we&apos;ll get back to you at the email you
            provided within one business day.
          </p>
          <Button asChild className="mt-6">
            <Link to="/">Back to home</Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-12 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Name" htmlFor="name" error={errors.name?.message}>
              <Input id="name" autoComplete="name" {...register('name')} />
            </FormField>
            <FormField label="Email" htmlFor="email" error={errors.email?.message}>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                {...register('email')}
              />
            </FormField>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              label="Company (optional)"
              htmlFor="company"
              error={errors.company?.message}
            >
              <Input id="company" autoComplete="organization" {...register('company')} />
            </FormField>
            <FormField
              label="Phone (optional)"
              htmlFor="phone"
              error={errors.phone?.message}
            >
              <Input id="phone" type="tel" autoComplete="tel" {...register('phone')} />
            </FormField>
          </div>

          <FormField
            label="How can we help?"
            htmlFor="message"
            error={errors.message?.message}
          >
            <Textarea
              id="message"
              rows={6}
              placeholder="Tell us about your business, expected call volume, or anything else you'd like to know."
              {...register('message')}
            />
          </FormField>

          {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}

          <Button type="submit" size="lg" disabled={isSubmitting} className="w-full">
            {isSubmitting ? 'Sending…' : 'Send message'}
          </Button>
        </form>
      )}

      <div className="mt-10 text-center text-sm text-muted-foreground">
        <p>
          Prefer to talk?{' '}
          <a href="tel:+13158093245" className="text-foreground underline underline-offset-4">
            +1 (315) 809-3245
          </a>
        </p>
        <p className="mt-1">
          {brand.legalName} &middot; Chicago, IL, USA
        </p>
      </div>
    </section>
  );
}
